/**
 * Admin API 客户端
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { Quiz, QuizStats, RedeemCode, User, Attempt, AwardClaim, PaginatedResponse } from '@/types';

const ADMIN_TOKEN = 'admin-secret-token-2025';

// 创建 axios 实例
const apiClient: AxiosInstance = axios.create({
  baseURL: 'https://api-quiz-dev.ycidx.com/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'X-Admin-Token': ADMIN_TOKEN,
  },
});

// 响应拦截器 - 只处理网络错误，业务错误交给组件处理
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // 只在网络错误或5xx服务器错误时显示通用错误提示
    if (!error.response || error.response.status >= 500) {
      const message = error.response?.data?.message || error.message || '网络连接失败';
      toast.error(message);
    }
    return Promise.reject(error);
  }
);

// 题集管理 API
export const quizApi = {
  // 获取所有题集
  getQuizzes: async (): Promise<{ quizzes: Quiz[]; total: number }> => {
    const response = await apiClient.get('/admin/quizzes');
    return response.data;
  },

  // 获取单个题集
  getQuiz: async (quizId: string): Promise<Quiz> => {
    const response = await apiClient.get(`/admin/quiz/${quizId}`);
    return response.data;
  },

  // 创建题集
  createQuiz: async (quiz: Omit<Quiz, 'created_at' | 'updated_at'>): Promise<{ success: boolean; quiz_id: string }> => {
    const response = await apiClient.post('/admin/quiz', quiz);
    return response.data;
  },

  // 更新题集
  updateQuiz: async (
    quizId: string,
    quiz: Partial<Omit<Quiz, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<{ success: boolean }> => {
    const response = await apiClient.put(`/admin/quiz/${quizId}`, quiz);
    return response.data;
  },

  // 删除题集
  deleteQuiz: async (quizId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/admin/quiz/${quizId}`);
    return response.data;
  },

  // 获取题集统计
  getQuizStats: async (quizId?: string): Promise<QuizStats[]> => {
    const url = quizId ? `/admin/stats?quiz_id=${quizId}` : '/admin/stats';
    const response = await apiClient.get(url);
    return response.data;
  },
};

// 兑换码管理 API
export const codeApi = {
  // 获取兑换码列表
  getCodes: async (params: {
    quiz_id?: string | null;
    scope?: 'quiz' | 'global' | 'all';
    status?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<PaginatedResponse<RedeemCode>> => {
    const { quiz_id, scope, ...rest } = params;
    const query: Record<string, unknown> = { ...rest };

    if (quiz_id && quiz_id.trim().length > 0) {
      query.quiz_id = quiz_id;
      if (scope) {
        query.scope = scope;
      }
    } else {
      if (scope) {
        query.scope = scope;
      } else {
        query.scope = 'global';
      }
    }

    const response = await apiClient.get('/admin/codes', { params: query });
    return response.data;
  },

  // 导入兑换码
  importCodes: async (data: {
    quiz_id?: string | null;
    codes: string[];
  }): Promise<{ success: boolean; imported: number; scope?: string; quiz_id?: string | null }> => {
    const payload = {
      ...data,
      quiz_id: data.quiz_id ?? null,
    };
    const response = await apiClient.post('/admin/codes/import', payload);
    return response.data;
  },

  // 生成兑换码
  generateCodes: async (data: {
    quiz_id?: string | null;
    count: number;
    prefix?: string;
  }): Promise<{ success: boolean; codes: string[]; scope?: string; quiz_id?: string | null }> => {
    const payload = {
      ...data,
      quiz_id: data.quiz_id ?? null,
    };
    const response = await apiClient.post('/admin/codes/generate', payload);
    return response.data;
  },

  // 更新兑换码状态或题集绑定
  updateCode: async (
    codeId: string,
    data: {
      status?: 'available' | 'revoked';
      quiz_id?: string | null;
    }
  ): Promise<{ success: boolean; status?: string; quiz_id?: string | null }> => {
    const payload: Record<string, unknown> = {};
    if (data.status) {
      payload.status = data.status;
    }
    if (Object.prototype.hasOwnProperty.call(data, 'quiz_id')) {
      payload.quiz_id = data.quiz_id ?? null;
    }
    const response = await apiClient.patch(`/admin/codes/${codeId}`, payload);
    return response.data;
  },

  // 删除单个兑换码
  deleteCode: async (codeId: string): Promise<{ success: boolean; deleted: number }> => {
    const response = await apiClient.delete(`/admin/codes/${codeId}`);
    return response.data;
  },

  // 批量删除兑换码
  deleteCodes: async (params: {
    quiz_id?: string;
    scope?: 'global';
    status?: 'available' | 'revoked';
  }): Promise<{ success: boolean; deleted: number }> => {
    const response = await apiClient.delete('/admin/codes', { params });
    return response.data;
  },
};

export const userApi = {
  // 获取用户列表
  getUsers: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: 'active' | 'deleted' | 'all';
  } = {}): Promise<{ users: User[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> => {
    const response = await apiClient.get('/admin/users', { params });
    return response.data;
  },

  // 获取用户详情
  getUser: async (userId: string): Promise<User> => {
    const response = await apiClient.get(`/admin/users/${userId}`);
    return response.data;
  },

  // 软删除用户
  deleteUser: async (userId: string, reason: string): Promise<{
    success: boolean;
    user_id: string;
    deleted_at: string;
    reason: string;
  }> => {
    const response = await apiClient.patch(`/admin/users/${userId}/delete`, { reason });
    return response.data;
  },

  // 恢复用户
  restoreUser: async (userId: string, reason: string): Promise<{
    success: boolean;
    user_id: string;
    nickname: string;
    restored_at: string;
    reason: string;
  }> => {
    const response = await apiClient.post('/admin/users/deleted/restore', {
      user_id: userId,
      reason
    });
    return response.data;
  },
};

// 答题记录 API
export const attemptApi = {
  // 获取答题记录
  getAttempts: async (params: {
    quiz_id?: string;
    user_id?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{ attempts: Attempt[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> => {
    const response = await apiClient.get('/admin/attempts', { params });
    return response.data;
  },

  // 获取答题详情
  getAttempt: async (attemptId: string): Promise<Attempt> => {
    const response = await apiClient.get(`/admin/attempts/${attemptId}`);
    return response.data;
  },
};

// 奖品发放记录 API  
export const awardApi = {
  // 获取奖品发放记录
  getAwards: async (params: {
    quiz_id?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<PaginatedResponse<AwardClaim>> => {
    const response = await apiClient.get('/admin/awards', { params });
    return response.data;
  },
};

// 系统管理 API
export const systemApi = {
  // 执行SQL
  executeSQL: async (sql: string): Promise<{ success: boolean; result?: any }> => {
    const response = await apiClient.post('/admin/execute-sql', { sql });
    return response.data;
  },

  // 获取系统状态
  getSystemInfo: async (): Promise<{
    version: string;
    environment: string;
    database_status: string;
    total_quizzes: number;
    total_users: number;
    total_attempts: number;
    available_codes: number;
    display_timezone: string;
  }> => {
    const response = await apiClient.get('/admin/system');
    return response.data;
  },
};

export default apiClient;



